import { ToggleButtonGroup, ToggleButton } from '@evaneos/b2b-ui';
import { Link, useLocation } from 'react-router-dom';

import { setEntryPoint } from '../analytics/sessionStorage-utilities';

const toggleSx = {
    color: 'text.primary',
    '&.Mui-selected': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
    },
    '&.Mui-selected:hover': {
        backgroundColor: 'primary.dark',
        color: 'primary.contrastText',
    },
    textTransform: 'none',
};

type NavigationToggleProps = {
    inboxCount: number;
    selected: 'inbox' | 'pipeline' | 'todo';
};

/**
 * Get accessibility aria attributes for ToggleButtons.
 * They are being used as links for navigation. `aria-pressed` is invalid as
 * it should be used only on button elements or elements with a role button.
 * In this case, ToggleButtons are `Link`. We use `aria-current` instead as they
 * indicate which sub-page is currently active.
 * @param value - Link value
 * @param selectedValue - Selected value (which link is active)
 */
const getAccessibilityProps = (
    value: NavigationToggleProps['selected'],
    selectedValue: NavigationToggleProps['selected'],
) => {
    return {
        // aria-pressed is invalid on anchor elements; undefined removes the attribute MUI would otherwise set
        'aria-pressed': undefined,
        'aria-current': value === selectedValue ? 'page' : undefined,
    } satisfies { 'aria-pressed': undefined; 'aria-current'?: 'page' };
};

export const NavigationToggle = ({ inboxCount, selected }: NavigationToggleProps) => {
    const location = useLocation();
    return (
        <ToggleButtonGroup
            color="primary"
            value={selected}
            size="small"
            exclusive
            aria-label="Platform"
        >
            <ToggleButton
                data-testid="link-inbox"
                component={Link}
                to={{
                    pathname: '/homepage',
                    search: location.search,
                }}
                onClick={() => {
                    if (selected !== 'inbox') {
                        setEntryPoint('inbox_button');
                    }
                }}
                disableRipple
                sx={toggleSx}
                value="inbox"
                {...getAccessibilityProps('inbox', selected)}
            >
                Inbox ({inboxCount ? inboxCount : '0'})
            </ToggleButton>
            <ToggleButton
                component={Link}
                to={{ pathname: '/process/sales', search: location.search }}
                disableRipple
                sx={toggleSx}
                value="pipeline"
                {...getAccessibilityProps('pipeline', selected)}
            >
                Pipeline
            </ToggleButton>
            <ToggleButton
                component={Link}
                to={{ pathname: '/todo/today', search: location.search }}
                disableRipple
                sx={toggleSx}
                value="todo"
                {...getAccessibilityProps('todo', selected)}
            >
                To Do
            </ToggleButton>
        </ToggleButtonGroup>
    );
};
